import _                                                                  from 'underscore';
import classNames                                                         from 'classnames';
import React, { CSSProperties, FC, useEffect, useState }                  from 'react';
import { MutationOptions, QueryHookOptions, useLazyQuery, useMutation }   from '@apollo/react-hooks';
import { Button, Modal }                                                  from 'semantic-ui-react';
import { StoreQueries }                                                   from '../../../queries';
import { LoadingIndicator }                                               from '../util/loading/LoadingIndicator';
import { ClassValue }                                                     from 'classnames/types';
import { Align, GQLNode, KeyedObject, SystemEvent, UseStateHookFunction } from '../../../types';
import { MutationFunctionOptions }                                        from 'react-apollo';
import { StyledContainer, StyledContainerProps }                          from '../../containers';
import { useSystemModals, useSystemToasts }                               from '../../../hooks';
import { BaseMutationOptions }                                            from "@apollo/react-common/lib/types/types";


export interface QueryDialogProps {
    operationDisabled?: boolean,
    closeIsDisabled?: boolean,
    operationIsDangerous?: boolean,
    operationIsHidden?: boolean,
    scrolling?: boolean,
    overflow?: boolean,
    autoClose?: boolean,
    fadeIn?: boolean,
    mutation?: GQLNode,
    mutationOptions?: MutationFunctionOptions & BaseMutationOptions,
    query?: GQLNode,
    queryOptions?: QueryHookOptions,
    graphArgs?: object | Function,
    cancelLabel?: string,
    submitLabel?: string,
    successToastLabel?: string,
    errorToastLabel?: string,
    operationInTransitMessage?: string,
    showOperationInTransitMessage?: boolean,
    showOperationInTransitOnSubmit?: boolean,
    className?: ClassValue,
    headerComponent?: any,
    alignControls?: StyledContainerProps['textAlign'],
    styles?: CSSProperties,

    onComplete?( data: KeyedObject ): void,

    onError?( data: KeyedObject ): void,

    onClose?( e: SystemEvent ): void,

    onSubmit?( e: SystemEvent ): void,

    confirmationHandler?: QueryOpDialogConfirmationHandler,

    [ additionalProps: string ]: any
}

export type QueryOpDialogConfirmationHandler = ( setConfirmed: UseStateHookFunction<boolean> ) => void;

const defaultProps: QueryDialogProps = {
    operationDisabled:              false,
    closeIsDisabled:                false,
    operationIsDangerous:           false,
    operationIsHidden:              false,
    scrolling:                      false,
    overflow:                       false,
    autoClose:                      true,
    fadeIn:                         false,
    onSubmit:                       () => {},
    onComplete:                     () => {},
    onError:                        () => {},
    onClose:                        () => {},
    mutation:                       null,
    mutationOptions:                {} as MutationOptions,
    query:                          null,
    queryOptions:                   {} as QueryHookOptions,
    graphArgs:                      {},
    cancelLabel:                    'Cancel',
    submitLabel:                    'Submit',
    successToastLabel:              'Operation Succeeded',
    errorToastLabel:                'Operation Failed',
    operationInTransitMessage:      'Loading...',
    showOperationInTransitMessage:  true,
    showOperationInTransitOnSubmit: false,
    className:                      'padding-lg',
    headerComponent:                null,
    alignControls:                  Align.RIGHT,
    confirmationHandler:            null,
    styles:                         {},
}

const QueryOpDialog: FC<QueryDialogProps> = React.memo( ( { mutation, query, graphArgs, ...props } ) => {

    if( !mutation && !query ){
        throw new Error( 'No GraphQL query defined in props for component QueryOpDialog.' );
    }

    let toastActions                      = useSystemToasts(),
        modalActions                      = useSystemModals(),
        [ mutationFunc, mutationFuncReq ] = useMutation( mutation || StoreQueries.mutations.EMPTY_MUTATION, props.mutationOptions ),
        [ queryFunc, queryFuncReq ]       = useLazyQuery( query || StoreQueries.queries.EMPTY_QUERY, props.queryOptions ),
        [ confirmed, setConfirmed ]       = useState( false );

    useEffect( () => {

        if( getOperationReq().data ){
            handleOperationComplete( getOperationReq().data )
        }
    }, [
        queryFuncReq.data,
        mutationFuncReq.data
    ] );

    useEffect( () => {

        if( confirmed ){

            handleOperation( null );
        }

    }, [ confirmed ] )

    const getOperationFunc = () => {

        return !_.isNull( mutation ) ? mutationFunc : queryFunc;
    };

    const getOperationReq = () => {

        return !_.isNull( mutation ) ? mutationFuncReq : queryFuncReq;
    };

    const handleOnClose = ( e: SystemEvent ) => {

        if( props.autoClose ) modalActions.toggle( false );

        props.onClose( e );
    };

    const handleOperation = async ( e: SystemEvent ) => {

        if( !props.confirmationHandler || ( confirmed === true ) ){

            props.onSubmit( e );

            try{

                await getOperationFunc()( _.isFunction( graphArgs ) ? graphArgs() : graphArgs );

            } catch( e ) {

                if( !_.isFunction( props.onError ) ){

                    toastActions.error( 'Network Issue', e.message, { timeoutMS: 0 } );

                } else {

                    props.onError( e );
                }
            }

        } else {

            props.confirmationHandler( setConfirmed );
        }
    };

    const handleOperationComplete = ( res: KeyedObject ) => {

        if( !res.errors ){

            if( props.successToastLabel ){
                toastActions.success( props.successToastLabel );
            }

            props.onComplete( res );

        } else {

            if( props.errorToastLabel ){
                toastActions.error( props.errorToastLabel, res.errors, { timeoutMS: 0 } );
            }

            props.onError( res );

        }

        setConfirmed( false );
    };

    if( props.showOperationInTransitMessage && getOperationReq().loading ){
        return (
            <div className={ classNames( 'padding-lg', { 'fade-in': props.fadeIn } ) }>
                <LoadingIndicator className={ 'padding-lg' } label={ props.operationInTransitMessage } isActive={ true } inverted/>
            </div>
        );
    }

    let contentClasses     = classNames( { 'fade-in': props.fadeIn }, props.className ),
        hasHeader          = !_.isNull( props.headerComponent ),
        modalContentStyles = {
            borderTopLeftRadius:  '0.2857rem',
            borderTopRightRadius: '0.2857rem',
            overflow:             props.overflow ? 'visible' : 'hidden',
            ...props.styles
        };

    return (
        <>
            { props.headerComponent }

            <Modal.Content scrolling={ props.scrolling } className={ contentClasses } style={ !hasHeader ? modalContentStyles : {} }>

                { props.children }

            </Modal.Content>
            <Modal.Actions>
                <StyledContainer textAlign={ props.alignControls }>

                    <Button key='close'
                            basic={ true }
                            content={ props.cancelLabel }
                            disabled={ props.closeIsDisabled || getOperationReq().loading }
                            onClick={ handleOnClose }/>

                    { !props.operationIsHidden && (
                        <Button key='operation'
                                content={ props.submitLabel }
                                primary={ !props.operationIsDangerous }
                                negative={ props.operationIsDangerous }
                                disabled={ props.operationDisabled }
                                loading={ props.showOperationInTransitOnSubmit && getOperationReq().loading }
                                onClick={ handleOperation }/>
                    ) }

                </StyledContainer>

            </Modal.Actions>
        </>
    )
} );

QueryOpDialog.defaultProps = defaultProps;

export { QueryOpDialog };
